import React, { useState } from 'react'
import { useCookies } from 'react-cookie'

import { DeckResponse } from 'services/apiTypes/deck.types'
import { CardType, OWNED } from 'types/deck'

import DeckService from 'services/deck.service'
import ToastService from 'services/toast.service'

import { getCardDefaultCategoryFromCard } from 'utils/CategoryGenerator'

import Icon from 'components/elements/Icon'
import BasicCard from 'components/card/BasicCard'
import ArchidektDropdown from 'components/elements/ArchidektDropdown'
import CornerOwned from 'components/card/controlsAndInfo/cornerOwned'
import AddToOtherDeck from 'components/card/cardForm/addToOtherDeck'

import styles from './diffCard.module.scss'
import AddCardsModal from 'components/collectionV2/addCards/Modal'

type Props = {
  card: CardType
  otherDeck?: DeckResponse
  deckA?: boolean
  className?: string
}

const DiffCard = ({ card, deckA, otherDeck, className = '' }: Props) => {
  const [{ tbId: userId }] = useCookies(['tbId'])

  const [outOfDate, setOutOfDate] = useState(false)
  const [addToOtherDeckModal, setAddToOtherDeckModal] = useState(false)
  const [addToCollectionModal, setAddToCollectionModal] = useState(false)
  const [collectionOwnedStatus, setCollectionOwnedStatus] = useState(card.owned)

  // Use undefined for the class if neight deckA nor deckB are specified
  const haloClass = deckA !== undefined ? (deckA ? styles.cardA : styles.cardB) : ''

  const ownsOtherDeck = userId === `${otherDeck?.owner.id}` && !otherDeck?.snapshotMeta
  const hideAddToDeck = deckA === undefined || !ownsOtherDeck

  const handleAddToOtherDeck = (categoryName?: string) => {
    if (!otherDeck) return

    const category = categoryName || getCardDefaultCategoryFromCard(card)

    DeckService.add(otherDeck.id, [{ ...card, colorLabel: { name: '', color: '#656565' }, categories: [category] }])
      .then(() => {
        setOutOfDate(true)
        ToastService.create(`${card.name} added to ${otherDeck.name}`, 'Deck Differences', 'success')
      })
      .catch(() => ToastService.create(`Adding ${card.name} to ${otherDeck.name} failed.`))
  }

  const handleOnAddedCardsToOtherDeck = (addedToDeckName: string) => {
    if (addedToDeckName === otherDeck?.name) setOutOfDate(true)
  }

  // prettier-ignore
  const openCardpage = (<><Icon name='arrow right' />Open card page</>)
  // prettier-ignore
  const addToCollection = (<><Icon name='address book' />Add to collection</>)
  // prettier-ignore
  const addToMainboard = (<><Icon name='add' />Add to Mainboard</>)
  // prettier-ignore
  const addToMaybeboard = (<><Icon name='add' />Add to Maybeboard</>)
  // prettier-ignore
  const addToSideboard = (<><Icon name='add' />Add to Sideboard</>)
  // prettier-ignore
  const addToOtherDeck = (<><Icon name='add' />Add to other deck</>)
  // prettier-ignore
  const deckHeader = (<><span className={deckA ? styles.bSplotch : styles.aSplotch} /> Deck {deckA ? 'B' : 'A'}</>)

  return (
    <>
      <div className={`${haloClass} ${styles.container}  ${className}`}>
        <div className={styles.card}>
          <BasicCard card={card} className={styles.basicCard} />
          {userId && <CornerOwned owned={collectionOwnedStatus} />}
        </div>
        <div className={styles.controls}>
          <ArchidektDropdown
            menuClassName={styles.dropdownMenu}
            options={[
              { type: 'header', label: deckHeader, hidden: hideAddToDeck },
              { label: addToMainboard, onClick: () => handleAddToOtherDeck(), hidden: hideAddToDeck },
              { label: addToMaybeboard, onClick: () => handleAddToOtherDeck('Maybeboard'), hidden: hideAddToDeck },
              { label: addToSideboard, onClick: () => handleAddToOtherDeck('Sideboard'), hidden: hideAddToDeck },
              { type: 'spacer', hidden: hideAddToDeck },
              { label: addToOtherDeck, onClick: () => setAddToOtherDeckModal(true) },
              { type: 'spacer' },
              { label: openCardpage, to: `/card/?uid=${card.uid}` },
              { label: addToCollection, onClick: () => setAddToCollectionModal(true) },
            ]}>
            <button className={`${styles.button} ${styles.blue}`}>
              <Icon name="ellipsis vertical" />
            </button>
          </ArchidektDropdown>
        </div>
        {outOfDate && <div className={styles.outdated}>Modified</div>}
      </div>
      <AddCardsModal
        open={addToCollectionModal}
        onClose={() => setAddToCollectionModal(false)}
        cardToAdd={card}
        onCardCreatedOrUpdated={updatedCollectionCard => {
          const ownedStatus = updatedCollectionCard.card.uid === card.uid ? OWNED.YES : OWNED.PRINTING

          setCollectionOwnedStatus(ownedStatus)
        }}
      />
      <AddToOtherDeck
        parentControlled
        card={card}
        open={addToOtherDeckModal}
        onClose={() => setAddToOtherDeckModal(false)}
        onAdded={handleOnAddedCardsToOtherDeck}
      />
    </>
  )
}

export default DiffCard
